import * as yup from 'yup';

import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  COMPLAINT,
  ONLINE_BOOKING,
  categoryList,
  complaintSubCategoryList,
  onlineSubCategoryList,
} from '@/constants/contact-us';
import {
  ContactUsModel,
  useCreateContactUs,
  useFileService,
  useGetSettings,
} from '@/logic/services';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  checkIfPhoneNumberIsValid,
  country,
  handleError,
  is500,
  muviPhoneNumberTrimmer,
} from '@/logic/shared';
import { useAuth, useCities, useGuest, useSnack } from '@/logic/state';
import { useCallback, useEffect, useRef, useState } from 'react';

import { FileUploadComponent } from '@/components/shared/upload-file-component/upload-file-component';
import InputAdornmentComponent from '@/components/shared/Input-adornment-component';
import ReCAPTCHA from 'react-google-recaptcha';
import RequiredGrayIcon from '@/components/shared/required-gray-icon';
import Seo from '@/components/seo/seo';
import { SeoPagesEnum } from '@/constants/seo-pages-enum';
import { MuiTelInput, MuiTelInputCountry, MuiTelInputInfo } from 'mui-tel-input';
import { excludedCountriesList } from '@/constants/phone-numbers';
import { findSeoInsidePage } from '@/logic/helpers/find-seo-inside-page';
import getConfig from 'next/config';
import getLangHeaders from '@/logic/helpers/get-lang-headers';
import isObject from 'lodash/isObject';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useCity } from '@/hooks';
import { useForm } from 'react-hook-form';
import { useGetCinemas } from '@/logic/services/cinemas';
import { useGetPagesSeoMeta } from '@/logic/services/seo';
import { useRouter } from 'next/router';
import { yupResolver } from '@hookform/resolvers/yup';
import { ContactFormSuccessModel, EventTypes } from '@/logic/types/data-layer-models';
import { triggerGAEvent } from '@/logic/shared/data-layer-events';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const ContactUs: NextPage<{
  path: string;
}> = ({ path }) => {
  const { data: seoDataList } = useGetPagesSeoMeta();
  const seoPage = findSeoInsidePage(seoDataList?.pageMetaData, SeoPagesEnum.contactUs);

  const theme = useTheme();
  const intl = useIntl();
  const { locale, asPath } = useRouter();
  const setSnack = useSnack(s => s.setSnack, shallow);

  const [countryCode_contact, setCountryCode_contact] = useState('KSA');
  const user = useAuth(s => s.user, shallow);
  const guest = useGuest(s => s.guest, shallow);
  const { cityId, getCity } = useCity();
  const [selectedCity, setSelectedCity] = useState(cityId);
  const [selectedCinema, setSelectedCinema] = useState<string>();
  const [errorFile, setErrorFile] = useState<boolean>();
  const [recaptchaValue, setRecaptchaValue] = useState<string>('');
  const [fileInitialValue, setFileInitialValue] = useState<string[]>(undefined);
  const [submittedImage, setSubmittedImage] = useState<File | string | undefined>(
    undefined
  );
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [selectedCountry, setSelectedCountry] = useState<country>(null);

  const cities = useCities(s => s.cities, shallow);

  const { publicRuntimeConfig } = getConfig();
  const { data: allCmsSetting } = useGetSettings();
  const { data: cinemas } = useGetCinemas({
    params: {
      cityId: selectedCity,
    },
  });

  const uploadFile = useFileService();

  const getSubmittedFormData = useCallback(() => {
    const formData = new FormData();
    if (isObject(submittedImage)) {
      formData.append('file', submittedImage);
      return formData;
    }
    return undefined;
  }, [submittedImage]);

  const schema = yup.object({
    phoneNumber: yup
      .string()
      .test(
        'test-valid-phone',
        intl.formatMessage({ id: 'global.phoneNumber.notValid' }),
        value => {
          if (value) {
            return checkIfPhoneNumberIsValid(value, countryCode_contact);
          }
        }
      ),
    customerName: yup.string().trim().required().min(2).max(101), //because it includes first & last name
    // .matches(/^[aA-zZ\s\u0621-\u064A]+$/),
    email: yup.string().email().required(),
    // .matches(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/),
    cityId: yup.string().required(),
    cinemaId: yup.string().required(),
    category: yup.string().required(),
    subject: yup.string().trim().required().min(2).max(50),
    description: yup.string().trim().required().min(2).max(200),
    subcategory: yup.string().when('category', {
      is: (category: string) => category === COMPLAINT || category === ONLINE_BOOKING,
      then: yup.string().required(),
    }),
  });

  const {
    register,
    setValue,
    handleSubmit,
    trigger,
    getValues,
    watch,
    reset,
    clearErrors,
    resetField,
    formState: { errors, touchedFields, isValid },
  } = useForm<ContactUsModel>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      customerName: user?.firstName ? user?.firstName + ' ' + user?.lastName : '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || undefined,
      cityId: `${selectedCity}` as string,
      cinemaId: null,
      category: '',
      subject: '',
      description: '',
      subcategory: '',
    },
  });

  const createContactUs = useCreateContactUs();
  const onSubmit = handleSubmit(
    async ({
      customerName,
      phoneNumber,
      cityId,
      cinemaId,
      category,
      subject,
      description,
      email,
      subcategory,
    }) => {
      const contactUsData: ContactUsModel = {
        customerName: customerName,
        phoneNumber: user
          ? phoneNumber
          : muviPhoneNumberTrimmer(phoneNumber, selectedCountry.dialCode),
        cityId: +cityId,
        cinemaId: +cinemaId,
        category: category,
        subcategory: subcategory,
        description: description,
        subject: subject,
        email: email,
        recaptchaResponse: recaptchaValue,
        fileUrl: '',
      };
      const submittedFormData = getSubmittedFormData();
      Boolean(submittedImage)
        ? uploadFile.safeMutateAsync(
            { data: submittedFormData },
            {
              onSuccess: res => {
                sendData(contactUsData, res?.url);
              },
            }
          )
        : sendData(contactUsData, undefined);
    }
  );

  const sendData = useCallback(
    async (contactUsData, urlFile) => {
      setFileInitialValue(urlFile);
      await createContactUs.safeMutateAsync(
        {
          data: { ...contactUsData, fileUrl: urlFile },
        },
        {
          onSuccess: () => {
            const data: ContactFormSuccessModel = {
              event: EventTypes.CONTACT_FORM_SUCCESS,
              page_section: asPath,
              page_title: document.title,
              page_url: document.URL,
              lead_type: intl.formatMessage({
                id: `contact_us.${contactUsData.category}`,
              }),
              mall: cinemas.cinema.find(cinema => cinema.id === contactUsData.cinemaId)
                .name,
              location: getCity(cityId)?.name,
            };
            triggerGAEvent(data);
            setSnack({
              isOpen: true,
              message: 'contact_us.created_successfully',
              type: 'success',
            });
            close();
            reset();
            if (user) {
              setValue('phoneNumber', user?.phoneNumber ?? '');
              setValue('email', user?.email ?? '');
              setValue(
                'customerName',
                user?.firstName ? user?.firstName + ' ' + user?.lastName : ''
              );
            }
            recaptchaRef.current?.reset();
            setSelectedCity(cityId || null);
            setSelectedCinema(null);
            setRecaptchaValue('');
            setFileInitialValue(undefined);
            setSubmittedImage(undefined);
            setErrorFile(false);
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createContactUs, reset, setSnack, user?.preferredCityId]
  );

  const handleUploadFile = useCallback((files: File[]) => {
    if (files.length > 0) {
      setSubmittedImage(files[0]);
    } else {
      setSubmittedImage(undefined);
    }
  }, []);

  const onChangeCaptcha = useCallback(
    value => {
      setRecaptchaValue(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue]
  );

  useEffect(() => {
    setSelectedCinema(null);
  }, [selectedCity]);

  useEffect(() => {
    clearErrors('customerName');
    clearErrors('email');
    if (user) {
      setValue('phoneNumber', user?.phoneNumber ?? '');
      setValue('email', user?.email ?? '');
      setValue(
        'customerName',
        user?.firstName ? user?.firstName + ' ' + user?.lastName : ''
      );

      setValue('cityId', user?.preferredCityId ?? null);
      setSelectedCity(user?.preferredCityId ?? null);
    } else {
      resetField('phoneNumber');
      resetField('customerName');
      resetField('email');
      setValue('phoneNumber', '');
      setValue('email', '');
      setValue('customerName', '');
      setValue('category', '');
      setValue('description', '');
      setValue('subject', '');
      setValue('subcategory', '');
      setValue('cinemaId', null);
      setSelectedCinema(null);
      setValue('cityId', `${guest?.preferredCityId}`);
      setSelectedCity(guest?.preferredCityId ?? null);
      setFileInitialValue(undefined);
      setSubmittedImage(undefined);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const subCategories =
    getValues('category') === COMPLAINT
      ? complaintSubCategoryList
      : getValues('category') === ONLINE_BOOKING
      ? onlineSubCategoryList
      : [];

  // const initFile = useMemo(() => fileInitialValue || submittedImage, [fileInitialValue,submittedImage]);

  return (
    <>
      <Seo
        title={
          seoPage?.titleTag ||
          intl.formatMessage({
            id: 'contact_us',
          })
        }
        canonical={seoPage?.canonicalURL}
        pagePath={path}
        description={seoPage?.descriptionTag || allCmsSetting?.contactUsDescription}
        hasFollow={seoPage?.hasFollow}
        hasIndex={seoPage?.hasIndex}
      />
      <Box
        component='section'
        sx={{
          mb: pxToRem(122),
        }}
      >
        <Typography
          component='h1'
          sx={{
            fontSize: { tablet: pxToRem(30), mobile: '18px' },
            color: theme.palette.grey[80],
            my: pxToRem(52),
            fontWeight: 700,
            px: {
              laptop: pxToRem(128),
              tablet: pxToRem(40),
              mobile: '16px',
            },
            [theme.breakpoints.down('tablet')]: {
              mt: '32px',
              mb: '24px',
            },
          }}
        >
          <FormattedMessage id='contact_us' />
        </Typography>

        <Box
          sx={{
            marginBlockEnd: 5,
            background: theme.palette.layout.bgSecondary,
            maxWidth: '1102px',
            pt: { tablet: pxToRem(50), mobile: '16px' },
            px: { tablet: pxToRem(135), mobile: '16px' },
            pb: { tablet: pxToRem(44), mobile: '25px' },
            mx: 'auto',
            mb: pxToRem(23),
            borderRadius: pxToRem(25),
            [theme.breakpoints.down(1134)]: {
              m: pxToRem(16),
              mt: pxToRem(40),
            },
            mt: { tablet: pxToRem(40), mobile: '24px' },
          }}
        >
          <Typography
            component='h4'
            sx={{
              fontSize: { tablet: pxToRem(28), mobile: '20px' },
              color: theme.palette.primary.main,
              mb: { tablet: pxToRem(41), mobile: '8px' },
              lineHeight: { tablet: '32px', mobile: '30px' },
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            <FormattedMessage id='contact_us.stay_connect' />
          </Typography>
          {allCmsSetting?.contactUsDescription && (
            <Typography
              component='p'
              sx={{
                fontSize: { tablet: pxToRem(24), mobile: '14px' },
                color: theme.palette.grey[70],
                mb: { tablet: pxToRem(24), mobile: '24px' },
                lineHeight: { tablet: '30px', mobile: '24px' },
                fontWeight: 400,
                textAlign: 'center',
              }}
            >
              {allCmsSetting.contactUsDescription}
            </Typography>
          )}
          <Box
            component='div'
            sx={{ display: 'flex', mb: { tablet: pxToRem(16), mobile: '20px' } }}
          >
            <RequiredGrayIcon />
            <Typography
              component='p'
              sx={{
                fontSize: '14px',
                color: theme.palette.grey[50],

                lineHeight: '24px',
                fontWeight: 400,
              }}
            >
              <FormattedMessage id='global.all_required' />
            </Typography>
          </Box>

          <Box
            sx={{
              width: '100%',
              mt: 3,
              '&.MuiGrid-root>.MuiGrid-item': {
                pt: { tablet: pxToRem(32), mobile: '24px' },
              },
            }}
          >
            <Grid container spacing={2}>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <TextField
                    id='contact-us-customerName'
                    disabled={Boolean(user?.firstName && user?.lastName)}
                    label={<FormattedMessage id='global.full_name' />}
                    variant='outlined'
                    {...register('customerName')}
                    error={(errors.customerName && touchedFields.customerName) ?? false}
                    InputProps={{
                      endAdornment: (
                        <InputAdornmentComponent
                          errorCondition={
                            errors.customerName && touchedFields.customerName
                          }
                        />
                      ),
                    }}
                    sx={{
                      '& fieldset span': {
                        opacity: user?.firstName ? '1!important' : '0',
                        transform: user?.firstName ? 'translateY(-5px)' : 'translateY(0)',
                      },
                      '&  label': {
                        opacity: !user?.firstName ? '1!important' : '0',
                      },
                    }}
                  />
                  <FormHelperText error>
                    {touchedFields.customerName &&
                      (errors?.customerName?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : errors?.customerName?.type === 'min' ||
                        errors?.customerName?.type === 'max' ? (
                        <>
                          <FormattedMessage id='global.please.enter.valid' />
                          &nbsp;
                          <FormattedMessage id='global.full_name' />
                          <FormattedMessage id='global.min.two.max.fifty' />
                        </>
                      ) : (
                        errors?.customerName?.type === 'matches' && (
                          <FormattedMessage id='global.only.chars.allowed' />
                        )
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <TextField
                    id='contact-us-email'
                    {...register('email')}
                    disabled={Boolean(user?.email)}
                    label={<FormattedMessage id='global.email' />}
                    variant='outlined'
                    error={!!(touchedFields.email && errors?.email)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornmentComponent
                          errorCondition={errors.email && touchedFields.email}
                        />
                      ),
                    }}
                    sx={{
                      '& fieldset span': {
                        opacity: user?.email ? '1!important' : '0',
                        transform: user?.email ? 'translateY(-5px)' : 'translateY(0)',
                      },
                      '&  label': {
                        opacity: !user?.email ? '1!important' : '0',
                      },
                    }}
                  />
                  <FormHelperText error>
                    {touchedFields.email &&
                      (errors?.email?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : (
                        (errors?.email?.type === 'email' ||
                          errors?.email?.type === 'matches') && (
                          <FormattedMessage id='global.validation.email' />
                        )
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl variant='standard'>
                  <MuiTelInput
                    excludedCountries={
                      excludedCountriesList.map(c =>
                        c.toUpperCase()
                      ) as MuiTelInputCountry[]
                    }
                    // MuiTelInput is strictly controlled — bind to the watched RHF
                    // value so typing reflects reliably (MUI 7 migration).
                    value={watch('phoneNumber') || ''}
                    error={
                      (errors?.phoneNumber?.type === 'test-valid-phone' &&
                        touchedFields.phoneNumber) ??
                      false
                    }
                    id='phone'
                    InputLabelProps={{
                      sx: {
                        backgroundColor: 'white',
                        paddingInline: '6px',
                      },
                    }}
                    sx={{
                      '& svg': {
                        height: '1em',
                        legend: {
                          width: 0,
                        },
                      },
                    }}
                    defaultCountry={'SA'}
                    dir='ltr'
                    {...register('phoneNumber')}
                    label={<FormattedMessage id='global.phone_number' />}
                    variant={'outlined'}
                    disabled={Boolean(user?.phoneNumber)}
                    onChange={(value: string, info: MuiTelInputInfo) => {
                      setSelectedCountry({
                        countryCode: (info.countryCode ?? '').toLowerCase(),
                        dialCode: info.countryCallingCode ?? '',
                        name: '',
                      });
                      setValue('phoneNumber', value);
                      const field = 'phoneNumber';
                      const c = info.countryCode?.toString().toUpperCase();
                      if (field.includes('contact')) {
                        setCountryCode_contact(c);
                      }
                    }}
                    // mui-tel-input v9 omits InputProps; slotProps.input is spread
                    // after the flag startAdornment, so passing only endAdornment
                    // restores the inline error icon without losing the flag.
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornmentComponent
                            errorCondition={
                              errors.phoneNumber?.type === 'test-valid-phone' &&
                              touchedFields.phoneNumber
                            }
                          />
                        ),
                      },
                    }}
                  />
                  <FormHelperText error>
                    {errors.phoneNumber?.type === 'test-valid-phone' &&
                      touchedFields.phoneNumber && (
                        <FormattedMessage id='global.phoneNumber.notValid' />
                      )}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <InputLabel
                    id='city-label'
                    error={(errors.cityId && touchedFields.cityId) ?? false}
                  >
                    <FormattedMessage id='global.select_city' />
                  </InputLabel>

                  <Select
                    labelId='city-label'
                    id='contact-us-city'
                    label={<FormattedMessage id='global.select_city' />}
                    value={(selectedCity as unknown as string) || getValues().cityId}
                    error={Boolean(errors.cityId)}
                    {...register('cityId')}
                    displayEmpty
                    onChange={(e: SelectChangeEvent) => {
                      setSelectedCity(e.target.value as unknown as number);
                      setValue('cityId', e.target.value as string);
                      trigger();
                    }}
                    MenuProps={{
                      BackdropProps: {
                        sx: {
                          backgroundColor: 'transparent',
                          backdropFilter: 'none',
                        },
                      },
                    }}
                    renderValue={() => {
                      return (
                        <Typography component='span'>
                          {
                            cities?.citiesList?.find(city => city.id === selectedCity)
                              ?.name
                          }
                        </Typography>
                      );
                    }}
                  >
                    {cities?.citiesList?.map(city => (
                      <MenuItem key={city.id} value={city.id}>
                        <Radio checked={selectedCity === city.id} />
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>
                    {touchedFields.cityId &&
                      (errors?.cityId?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : (
                        ''
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <InputLabel
                    id='cinema-label'
                    error={(errors.cinemaId && touchedFields.cinemaId) ?? false}
                    sx={{
                      color:
                        Boolean(selectedCity) || Boolean(getValues().cityId)
                          ? theme.palette.grey[60]
                          : theme.palette.grey[30],
                    }}
                  >
                    <FormattedMessage id='global.select_cinema' />
                  </InputLabel>

                  <Select
                    labelId='cinema-label'
                    id='contact-us-cinema'
                    label={<FormattedMessage id='global.select_cinema' />}
                    value={(selectedCinema as unknown as string) || ''}
                    disabled={!Boolean(selectedCity) && !Boolean(getValues().cityId)}
                    {...register('cinemaId')}
                    error={Boolean(errors.cinemaId) && touchedFields.cinemaId}
                    onChange={(e: SelectChangeEvent) => {
                      setSelectedCinema(e.target.value as unknown as string);
                      setValue('cinemaId', e.target.value as unknown as number);
                      trigger();
                    }}
                    MenuProps={{
                      BackdropProps: {
                        sx: {
                          backgroundColor: 'transparent',
                          backdropFilter: 'none',
                        },
                      },
                    }}
                    displayEmpty
                    renderValue={() => {
                      return (
                        <Typography component='span'>
                          {
                            cinemas?.cinema?.find(cinema => cinema.id === +selectedCinema)
                              ?.name
                          }
                        </Typography>
                      );
                    }}
                  >
                    {cinemas?.cinema?.map(cinema => (
                      <MenuItem key={cinema.id} value={cinema.id}>
                        <Radio checked={+selectedCinema === cinema.id} />
                        {cinema.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>
                    {touchedFields.cinemaId &&
                      (errors?.cinemaId?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : (
                        ''
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FormControl>
                  <InputLabel
                    id='category-label'
                    error={(errors.category && touchedFields.category) ?? false}
                  >
                    <FormattedMessage id='contact_us.category' />
                  </InputLabel>
                  <Select
                    labelId='category-label'
                    id='contact-us-category'
                    label={<FormattedMessage id='contact_us.category' />}
                    value={getValues('category')}
                    {...register('category')}
                    error={Boolean(errors.category) && touchedFields.category}
                    onChange={(e: SelectChangeEvent) => {
                      setValue('category', e.target.value as string);
                      resetField('subcategory');
                      trigger();
                    }}
                    MenuProps={{
                      BackdropProps: {
                        sx: {
                          backgroundColor: 'transparent',
                          backdropFilter: 'none',
                        },
                      },
                    }}
                    displayEmpty
                    renderValue={() => {
                      return (
                        <Typography component='span'>
                          {getValues('category') ? (
                            <FormattedMessage
                              id={`contact_us.${categoryList.find(
                                category => category === getValues('category')
                              )}`}
                            />
                          ) : (
                            ''
                          )}
                        </Typography>
                      );
                    }}
                  >
                    {categoryList.map((item, index) => {
                      return (
                        <MenuItem value={item} key={index}>
                          <Radio checked={item === getValues('category')} />
                          <FormattedMessage id={`contact_us.${item}`} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <FormHelperText error>
                    {touchedFields.category &&
                      (errors?.category?.type === 'required' ? (
                        <FormattedMessage id='global.required' />
                      ) : (
                        ''
                      ))}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {(getValues('category') === COMPLAINT ||
                getValues('category') === ONLINE_BOOKING) && (
                <>
                  <Grid item tablet={6} mobile={12} laptop={6}>
                    <FormControl>
                      <InputLabel
                        id='subcategory-label'
                        error={(errors.subcategory && touchedFields.subcategory) ?? false}
                      >
                        <FormattedMessage id='contact_us.subcategory' />
                      </InputLabel>
                      <Select
                        labelId='subcategory-label'
                        id='contact-us-subcategory'
                        label={<FormattedMessage id='contact_us.subcategory' />}
                        value={getValues('subcategory')}
                        {...register('subcategory')}
                        error={Boolean(errors.subcategory) && touchedFields.subcategory}
                        onChange={(e: SelectChangeEvent) => {
                          setValue('subcategory', e.target.value as string);
                          trigger();
                        }}
                        MenuProps={{
                          BackdropProps: {
                            sx: {
                              backgroundColor: 'transparent',
                              backdropFilter: 'none',
                            },
                          },
                        }}
                        displayEmpty
                        renderValue={() => {
                          return (
                            <Typography component='span'>
                              {getValues('subcategory') ? (
                                <FormattedMessage
                                  id={`contact_us.${subCategories.find(
                                    subcategory =>
                                      subcategory === getValues('subcategory')
                                  )}`}
                                />
                              ) : (
                                ''
                              )}
                            </Typography>
                          );
                        }}
                      >
                        {subCategories.map((item, index) => {
                          return (
                            <MenuItem value={item} key={index}>
                              <Radio checked={item === getValues('subcategory')} />
                              <FormattedMessage id={`contact_us.${item}`} />
                            </MenuItem>
                          );
                        })}
                      </Select>
                      <FormHelperText error>
                        {touchedFields.subcategory &&
                          (errors?.subcategory?.type === 'required' ? (
                            <FormattedMessage id='global.required' />
                          ) : (
                            ''
                          ))}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item tablet={6} mobile={12} laptop={6}>
                    <FormControl>
                      <TextField
                        id='contact-us-subject'
                        label={
                          <>
                            <FormattedMessage id='contact_us.subject' />
                          </>
                        }
                        variant='outlined'
                        {...register('subject')}
                        error={(errors.subject && touchedFields.subject) ?? false}
                        InputProps={{
                          endAdornment: (
                            <InputAdornmentComponent
                              errorCondition={errors.subject && touchedFields.subject}
                            />
                          ),
                        }}
                      />
                      <FormHelperText error>
                        {touchedFields.subject &&
                          (errors?.subject?.type === 'required' ? (
                            <FormattedMessage id='global.required' />
                          ) : errors?.subject?.type === 'min' ||
                            errors?.subject?.type === 'max' ? (
                            <>
                              <FormattedMessage id='global.please.enter.valid' />
                              &nbsp;
                              <FormattedMessage id='global.min.two.max.fifty' />
                            </>
                          ) : (
                            errors?.subject?.type === 'matches' && (
                              <FormattedMessage id='global.only.chars.allowed' />
                            )
                          ))}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item tablet={6} mobile={12} laptop={6}>
                    <FormControl>
                      <TextField
                        id='contact-us-description'
                        label={
                          <>
                            <FormattedMessage id='contact_us.description' />
                          </>
                        }
                        variant='outlined'
                        {...register('description')}
                        error={(errors.description && touchedFields.description) ?? false}
                        InputProps={{
                          endAdornment: (
                            <InputAdornmentComponent
                              errorCondition={
                                errors.description && touchedFields.description
                              }
                            />
                          ),
                        }}
                        multiline
                        rows={3}
                        maxRows={3}
                      />
                      <FormHelperText error>
                        {touchedFields.description &&
                          (errors?.description?.type === 'required' ? (
                            <FormattedMessage id='global.required' />
                          ) : errors?.description?.type === 'min' ||
                            errors?.description?.type === 'max' ? (
                            <>
                              <FormattedMessage id='global.please.enter.valid' />
                              &nbsp;
                              <FormattedMessage id='global.min.two.max.200' />
                            </>
                          ) : (
                            errors?.description?.type === 'matches' && (
                              <FormattedMessage id='global.only.chars.allowed' />
                            )
                          ))}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </>
              )}
              {getValues('category') !== COMPLAINT &&
                getValues('category') !== ONLINE_BOOKING && (
                  <Grid item tablet={6} mobile={12} laptop={6}>
                    <FormControl>
                      <TextField
                        id='contact-us-subject'
                        {...register('subject')}
                        label={
                          <>
                            <FormattedMessage id='contact_us.subject' />
                          </>
                        }
                        variant='outlined'
                        {...register('subject')}
                        error={(errors.subject && touchedFields.subject) ?? false}
                        InputProps={{
                          endAdornment: (
                            <InputAdornmentComponent
                              errorCondition={errors.subject && touchedFields.subject}
                            />
                          ),
                        }}
                      />
                      <FormHelperText error>
                        {touchedFields.subject &&
                          (errors?.subject?.type === 'required' ? (
                            <FormattedMessage id='global.required' />
                          ) : errors?.subject?.type === 'min' ||
                            errors?.subject?.type === 'max' ? (
                            <>
                              <FormattedMessage id='global.please.enter.valid' />
                              &nbsp;
                              <FormattedMessage id='global.min.two.max.fifty' />
                            </>
                          ) : (
                            errors?.subject?.type === 'matches' && (
                              <FormattedMessage id='global.only.chars.allowed' />
                            )
                          ))}
                      </FormHelperText>
                    </FormControl>
                    <FormControl sx={{ pt: { tablet: '16px', mobile: '16px' } }}>
                      <TextField
                        id='contact-us-description'
                        label={
                          <>
                            <FormattedMessage id='contact_us.description' />
                          </>
                        }
                        variant='outlined'
                        {...register('description')}
                        error={(errors.description && touchedFields.description) ?? false}
                        InputProps={{
                          endAdornment: (
                            <InputAdornmentComponent
                              errorCondition={
                                errors.description && touchedFields.description
                              }
                            />
                          ),
                        }}
                        multiline
                        rows={3}
                        maxRows={3}
                      />
                      <FormHelperText error>
                        {touchedFields.description &&
                          (errors?.description?.type === 'required' ? (
                            <FormattedMessage id='global.required' />
                          ) : errors?.description?.type === 'min' ||
                            errors?.description?.type === 'max' ? (
                            <>
                              <FormattedMessage id='global.please.enter.valid' />
                              &nbsp;
                              <FormattedMessage id='global.min.two.max.200' />
                            </>
                          ) : (
                            errors?.description?.type === 'matches' && (
                              <FormattedMessage id='global.only.chars.allowed' />
                            )
                          ))}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
              <Grid item tablet={6} mobile={12} laptop={6}>
                <FileUploadComponent
                  accept={{
                    'image/png': ['.png'],
                    'image/jpeg': ['.jpeg', '.jpg'],
                    'application/pdf': ['.pdf'],
                  }}
                  imgType
                  handleFiles={handleUploadFile}
                  initValues={fileInitialValue || submittedImage}
                  isLoading={uploadFile.isLoading}
                  errorFile={errorFile}
                />
              </Grid>
              {!user?.phoneNumber && (
                <Grid item tablet={12} mobile={12} laptop={12}>
                  <FormControl
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',

                      '&.MuiFormControl-root  div > div > div ': {
                        mx: 'auto',
                      },
                    }}
                  >
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={publicRuntimeConfig.recaptchaClientKey}
                      onChange={onChangeCaptcha}
                      hl={locale}
                    />
                  </FormControl>
                </Grid>
              )}
              <Grid item tablet={12} mobile={12} laptop={12}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',

                    '& button': {},
                  }}
                >
                  <Button
                    variant='contained'
                    loading={createContactUs.isLoading}
                    sx={{
                      width: { tablet: pxToRem(178), mobile: '145px' },
                      height: { tablet: pxToRem(56), mobile: '56px' },
                      borderRadius: '16px',
                    }}
                    onClick={() => onSubmit()}
                    disabled={
                      !isValid ||
                      (!Boolean(recaptchaValue) && !Boolean(user?.phoneNumber) && !user)
                    }
                  >
                    <Typography
                      component='p'
                      sx={{
                        fontSize: pxToRem(16),
                        fontWeight: '700',
                        lineHeight: '24px',
                      }}
                    >
                      <FormattedMessage id='global.submit' />
                    </Typography>
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const queryClient = new QueryClient();

  try {
    await useGetPagesSeoMeta.fetchQuery(queryClient, {
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};
export default ContactUs;
